import type { Challenge } from '@/types/challenge';

const FALLBACK_DATE = '2026-03-31';

export function getChallengeDateKey(): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export async function loadTodayChallenge(): Promise<Challenge> {
  const todayKey = getChallengeDateKey();

  try {
    const response = await fetch(`/data/challenges/${todayKey}.json`);

    if (response.ok) {
      return await response.json();
    }

    console.warn(`Today's challenge not found, falling back to ${FALLBACK_DATE}.json`);
  } catch (error) {
    console.warn('Failed to fetch today challenge, using fallback instead:', error);
  }

  try {
    const fallbackResponse = await fetch(`/data/challenges/${FALLBACK_DATE}.json`);

    if (!fallbackResponse.ok) {
      throw new Error(`Fallback file missing: ${fallbackResponse.status}`);
    }

    const fallbackData = await fallbackResponse.json();

    return {
      ...fallbackData,
      id: `daily-${todayKey}`,
      date: todayKey,
    };
  } catch (error) {
    console.error('Failed to load fallback challenge:', error);

    return {
      id: `daily-${todayKey}`,
      date: todayKey,
      title: 'Daily Challenge',
      logLines: [
        '2026-03-30T13:41:22Z ERROR api-gateway',
        'connect ECONNREFUSED 10.1.4.22:5432',
        'retry attempt 1 failed',
        'retry attempt 2 failed',
        'upstream request aborted after 30000ms',
      ],
      correctAnswer: {
        classification: 'dependency_unavailable',
        severity: 'high',
        nextAction: 'investigate_database_service',
        confidence: 0.91,
        explanation:
          'The database service is refusing connections on port 5432, causing retries and upstream timeouts.',
      },
    } as Challenge;
  }
}

export type ChallengeStats = {
  challengeDate: string;
  attempts: number;
  correct: number;
  percentCorrect: number;
};

function getStatsKey(challengeDate: string): string {
  return `challenge-stats-${challengeDate}`;
}

export function getChallengeStats(challengeDate: string): ChallengeStats {
  if (typeof window === 'undefined') {
    return {
      challengeDate,
      attempts: 0,
      correct: 0,
      percentCorrect: 0,
    };
  }

  const raw = localStorage.getItem(getStatsKey(challengeDate));

  if (!raw) {
    return {
      challengeDate,
      attempts: 0,
      correct: 0,
      percentCorrect: 0,
    };
  }

  try {
    return JSON.parse(raw);
  } catch {
    return {
      challengeDate,
      attempts: 0,
      correct: 0,
      percentCorrect: 0,
    };
  }
}

export function submitChallengeResult(
  challengeDate: string,
  solvedCorrectly: boolean
): ChallengeStats {
  const current = getChallengeStats(challengeDate);

  const updatedAttempts = current.attempts + 1;
  const updatedCorrect = solvedCorrectly ? current.correct + 1 : current.correct;
  const percentCorrect =
    updatedAttempts > 0 ? Math.round((updatedCorrect / updatedAttempts) * 100) : 0;

  const updated: ChallengeStats = {
    challengeDate,
    attempts: updatedAttempts,
    correct: updatedCorrect,
    percentCorrect,
  };

  if (typeof window !== 'undefined') {
    localStorage.setItem(getStatsKey(challengeDate), JSON.stringify(updated));
  }

  return updated;
}

export function hasSubmittedResult(challengeDate: string): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(`submitted-${challengeDate}`) === 'true';
}

export function markSubmittedResult(challengeDate: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(`submitted-${challengeDate}`, 'true');
}